(() => {
  if (window.CartGiftRenderer) return;

  const SVG_NS = 'http://www.w3.org/2000/svg';
  const XLINK_NS = 'http://www.w3.org/1999/xlink';

  const getTemplate = (picker, tier) =>
    picker?.querySelector(
      `[data-gift-modal-template][data-gift-tier="${tier}"]`
    ) || null;

  const getAnimationRoot = (template) =>
    template?.content.querySelector('[data-gift-animation-template]')?.content || null;

  const createCaret = () => {
    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.classList.add('icon', 'icon-caret');
    svg.setAttribute('viewBox', '0 0 10 6');
    svg.setAttribute('aria-hidden', 'true');
    const path = document.createElementNS(SVG_NS, 'path');
    path.setAttribute('d', 'M1 1l4 4 4-4');
    path.setAttribute('fill', 'none');
    path.setAttribute('stroke', 'currentColor');
    path.setAttribute('stroke-linecap', 'round');
    path.setAttribute('stroke-linejoin', 'round');
    svg.appendChild(path);
    return svg;
  };

  const setProductImage = (image, product) => {
    if (!image) return;
    image.setAttribute('href', product.image);
    image.setAttributeNS(XLINK_NS, 'xlink:href', product.image);
  };

  const createOptionGroup = ({ optionIndex, optionName, product, selectedVariant, tier }) => {
    const position = optionIndex + 1;
    const values = [
      ...new Set(
        product.variants
          .map((variant) => String(variant.options?.[optionIndex] || ''))
          .filter(Boolean)
      ),
    ];
    if (!values.length) return null;

    const selectedValue =
      String(selectedVariant.options?.[optionIndex] || '') || values[0];
    const group = document.createElement('div');
    group.className = 'cart-gift-picker__option-group';
    group.dataset.giftOptionGroup = '';
    group.dataset.optionPosition = String(position);

    const label = document.createElement('p');
    label.className = 'cart-gift-picker__option-label';
    label.append(`${optionName} – `);
    const selectedLabel = document.createElement('strong');
    selectedLabel.dataset.giftOptionSelected = '';
    selectedLabel.textContent = selectedValue;
    label.appendChild(selectedLabel);
    group.appendChild(label);

    const selectWrap = document.createElement('div');
    selectWrap.className = 'cart-gift-picker__select-wrap';
    selectWrap.dataset.giftCustomSelect = '';

    const nativeSelect = document.createElement('select');
    nativeSelect.dataset.giftOption = '';
    nativeSelect.dataset.optionPosition = String(position);
    nativeSelect.setAttribute('aria-label', optionName);
    nativeSelect.tabIndex = -1;
    nativeSelect.hidden = true;
    values.forEach((value) => {
      const option = document.createElement('option');
      option.value = value;
      option.textContent = value;
      option.selected = value === selectedValue;
      nativeSelect.appendChild(option);
    });
    selectWrap.appendChild(nativeSelect);

    const listId = `GiftOptionList-${tier}-${product.productId}-${position}`;
    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'cart-gift-picker__select-trigger';
    trigger.dataset.giftSelectTrigger = '';
    trigger.setAttribute('aria-haspopup', 'listbox');
    trigger.setAttribute('aria-expanded', 'false');
    trigger.setAttribute('aria-controls', listId);
    const triggerValue = document.createElement('span');
    triggerValue.dataset.giftSelectValue = '';
    triggerValue.textContent = selectedValue;
    trigger.append(triggerValue, createCaret());
    selectWrap.appendChild(trigger);

    const options = document.createElement('div');
    options.id = listId;
    options.className = 'cart-gift-picker__select-options';
    options.dataset.giftSelectOptions = '';
    options.setAttribute('role', 'listbox');
    options.setAttribute('aria-label', optionName);
    options.hidden = true;
    values.forEach((value) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'cart-gift-picker__select-option';
      button.dataset.giftSelectOption = '';
      button.dataset.value = value;
      button.dataset.available = 'true';
      button.setAttribute('role', 'option');
      button.setAttribute('aria-selected', value === selectedValue ? 'true' : 'false');
      const text = document.createElement('span');
      text.textContent = value;
      button.appendChild(text);
      options.appendChild(button);
    });
    selectWrap.appendChild(options);
    group.appendChild(selectWrap);
    return group;
  };

  const renderVariantPanel = (panel, product, productIndex, tier) => {
    const availableVariants = product.variants.filter(
      (variant) => variant.available !== false && Number(variant.id) > 0
    );
    if (!panel || !availableVariants.length) return false;
    const selectedVariant = availableVariants[0];
    const titleId = `GiftVariantTitle-${tier}-${product.productId}`;
    const image = panel.querySelector('.cart-gift-picker__variant-image');
    const title = panel.querySelector('.cart-gift-picker__variant-title');
    const confirm = panel.querySelector('[data-gift-confirm]');
    if (!image || !title || !confirm) return false;

    panel.dataset.productId = String(product.productId);
    panel.dataset.productFamily = product.family;
    panel.dataset.giftVariantPanel = String(productIndex);
    panel.setAttribute('aria-labelledby', titleId);

    image.src = product.image;
    image.alt = product.title;
    image.removeAttribute('srcset');
    image.removeAttribute('sizes');
    title.id = titleId;
    title.textContent = product.title;

    panel.querySelector('.cart-gift-picker__option-groups')?.remove();
    const variantsHost =
      panel.querySelector('.visually-hidden[aria-hidden="true"]') ||
      document.createElement('div');
    variantsHost.className = 'visually-hidden';
    variantsHost.setAttribute('aria-hidden', 'true');
    variantsHost.replaceChildren();

    availableVariants.forEach((variant, index) => {
      const input = document.createElement('input');
      input.type = 'radio';
      input.name = `GiftVariant-${tier}-${product.productId}`;
      input.value = String(variant.id);
      input.dataset.giftVariant = '';
      input.dataset.options = JSON.stringify(variant.options || []);
      input.dataset.available = 'true';
      input.tabIndex = -1;
      input.checked = index === 0;
      variantsHost.appendChild(input);
    });

    if (!variantsHost.isConnected) panel.insertBefore(variantsHost, confirm);

    const optionCount = Math.max(
      product.optionNames?.length || 0,
      ...availableVariants.map((variant) => variant.options?.length || 0)
    );
    if (optionCount > 0) {
      const groups = document.createElement('div');
      groups.className = 'cart-gift-picker__option-groups';
      for (let optionIndex = 0; optionIndex < optionCount; optionIndex += 1) {
        const group = createOptionGroup({
          optionIndex,
          optionName: product.optionNames?.[optionIndex] || `Option ${optionIndex + 1}`,
          product,
          selectedVariant,
          tier,
        });
        if (group) groups.appendChild(group);
      }
      panel.insertBefore(groups, variantsHost);
    }
    confirm.disabled = false;
    return true;
  };

  const render = ({ picker, products, source, tier }) => {
    if (!picker || products?.length !== 3) return false;
    const template = getTemplate(picker, tier);
    const root = getAnimationRoot(template);
    if (!template || !root) return false;

    const families = products.map((product) => product.family);
    const productIds = products.map((product) => Number(product.productId));
    if (
      families.some((family) => !family) ||
      productIds.some((productId) => !productId) ||
      new Set(families).size !== 3 ||
      new Set(productIds).size !== 3
    ) {
      return false;
    }

    const fallbackImageSelectors = [
      '#product-card-green image',
      '#product-card-blue image',
      '#product-card-pink image',
    ];
    const targets = products.map((product, index) => {
      const productIndex = index + 1;
      const choice = root.querySelector(
        `[data-gift-choice][data-product-index="${productIndex}"]`
      );
      const panel = root.querySelector(
        `[data-gift-variant-panel="${productIndex}"]`
      );
      const productImage =
        root.querySelector(`[data-gift-product-image="${productIndex}"]`) ||
        root.querySelector(fallbackImageSelectors[index]);
      const panelReady = Boolean(
        panel?.querySelector('.cart-gift-picker__variant-image') &&
        panel?.querySelector('.cart-gift-picker__variant-title') &&
        panel?.querySelector('[data-gift-confirm]')
      );
      return { choice, panel, panelReady, product, productImage, productIndex };
    });
    if (targets.some(({ choice, panel, panelReady, productImage }) =>
      !choice || !panel || !panelReady || !productImage
    )) return false;

    let complete = true;
    targets.forEach(({ choice, panel, product, productImage, productIndex }) => {
      setProductImage(productImage, product);
      choice.dataset.productId = String(product.productId);
      choice.dataset.productFamily = product.family;
      choice.setAttribute(
        'aria-label',
        product.variants.length === 1
          ? `Add ${product.title} as your free gift`
          : `Choose a variant of ${product.title}`
      );
      if (!renderVariantPanel(panel, product, productIndex, tier)) complete = false;
    });
    if (!complete) return false;
    template.dataset.recommendationState = 'ready';
    template.dataset.recommendationSource = source;
    picker.dataset.giftSource = source;
    return true;
  };

  const hasFallbackChoices = (picker, tier) => {
    const root = getAnimationRoot(getTemplate(picker, tier));
    if (!root) return false;
    const choices = [...root.querySelectorAll('[data-gift-choice]')];
    const panels = [...root.querySelectorAll('[data-gift-variant-panel]')];
    return (
      choices.length === 3 &&
      panels.length === 3 &&
      choices.every((choice) => Number(choice.dataset.productId) > 0) &&
      panels.every((panel) =>
        [...panel.querySelectorAll('[data-gift-variant]')].some(
          (variant) => variant.dataset.available === 'true' && Number(variant.value) > 0
        )
      )
    );
  };

  window.CartGiftRenderer = Object.freeze({
    hasFallbackChoices,
    render,
  });
})();
